/**
 * conflictDiff — QUESTIONNAIRE-2.0-A-S2-γ-D-A (helper puro, stand-alone).
 *
 * Pure functions to classify a 409 conflict event and decide whether a
 * "Reload & retry" action is safe to offer. No fetch. No DOM. No mutation.
 *
 * Contract recap (analysis γ-D §5, §6, §15-16):
 *   Backend returns two flavors of 409:
 *     - version_conflict: payload includes current_version + expected_version.
 *     - status_conflict:  payload includes path + current_status + expected_previous_status.
 *
 * Drivers can be:
 *     - StatusChip click (status-only flip)
 *     - T1 confirm dialog (status flip with expected_previous_status)
 *     - BaseEditDialog confirm (value PATCH on base lang)
 *
 * The classifier maps the (attempt, conflictPayload, remoteSnapshot) triple
 * into a small set of well-defined cases that the modal renders against.
 */

// ──────────────────────────────────────────────────────────────────────────
// Conflict classifications. Keep these enum-like strings stable — the modal
// keys its rendering off them and Playwright drivers may assert on them via
// `data-conflict-class="..."`.
// ──────────────────────────────────────────────────────────────────────────
export const CONFLICT_CLASS = Object.freeze({
  // version_conflict + remote value at the attempted path is unchanged
  // (or attempt is status-only). Safe to refresh `if_version` and retry.
  VERSION_RETRY_SAFE: "version_retry_safe",
  // version_conflict + remote value at the attempted path differs from
  // what the client had cached. Retry would overwrite remote work → block.
  VERSION_RETRY_BLOCKED: "version_retry_blocked",
  // status_conflict: the path's current status already moved away from the
  // expected_previous_status. Retry is unsafe by definition.
  STATUS_CONFLICT: "status_conflict",
  // Fallback when payload is malformed or unrecognized.
  UNKNOWN: "unknown_conflict",
});

// Stable normalization for value comparisons (avoid whitespace-only diffs
// from line-ending normalization across OSes).
export const normalizeValue = (v) =>
  v == null ? "" : String(v).replace(/\r\n/g, "\n").trim();

// True when two textual values are equal under the normalization above.
export const valuesEqual = (a, b) => normalizeValue(a) === normalizeValue(b);

// Friendly status label for header chips / inline summary.
export const friendlyStatusLabel = (status) => {
  if (!status) return "—";
  const map = {
    draft: "Draft",
    pending_review: "Pending review",
    reviewed: "Reviewed",
    needs_update: "Needs update",
    missing: "Missing",
  };
  return map[status] || String(status);
};

// Format version delta as a human string.
export const formatVersionDelta = (local, remote) => {
  if (typeof local !== "number" || typeof remote !== "number") return "version info unavailable";
  if (local === remote) return `version ${local} (in sync)`;
  const diff = Math.max(0, remote - local);
  return `version ${local} → ${remote} (${diff} change${diff === 1 ? "" : "s"} behind)`;
};

// ──────────────────────────────────────────────────────────────────────────
// classifyConflict — main entry point for the classifier.
//
// @param {object}  params
// @param {object}  params.attempt          Last patch the client tried.
//   Shape: { path, value?, status?, expected_previous_status?, cachedRemoteValue? }
//   - `value` set        → driver = BaseEditDialog (or value PATCH).
//   - `status` set       → driver = StatusChip / T1.
//   - `cachedRemoteValue`→ the value the client had loaded before the attempt
//                          (used to detect "remote drifted on my path").
// @param {object}  params.conflictPayload  The 409 body from the backend.
//   Shape (version_conflict): { error, current_version, expected_version }
//   Shape (status_conflict):  { error, path, current_status, expected_previous_status }
// @param {object}  params.remoteSnapshot   What the client refreshed AFTER 409.
//   Shape: { version, valueAtPath?, statusAtPath?, lastEditedBy?, updatedAt? }
//
// @returns {{ kind, reason, canRetry }}
// ──────────────────────────────────────────────────────────────────────────
export const classifyConflict = ({ attempt, conflictPayload, remoteSnapshot } = {}) => {
  const errCode = conflictPayload?.error || "";

  // Status conflict — by definition, retry is unsafe.
  if (errCode === "errors.status_conflict") {
    return {
      kind: CONFLICT_CLASS.STATUS_CONFLICT,
      reason: `Path status already moved to "${friendlyStatusLabel(conflictPayload.current_status)}" remotely; your transition assumed "${friendlyStatusLabel(conflictPayload.expected_previous_status)}".`,
      canRetry: false,
    };
  }

  if (errCode === "errors.version_conflict") {
    // Status-only attempt (no `value`): retry is safe as long as the remote
    // status at the path didn't already advance past expected_previous_status.
    if (attempt && attempt.value === undefined && attempt.status !== undefined) {
      const remoteStatus = remoteSnapshot?.statusAtPath;
      const expectedPrev = attempt.expected_previous_status;
      if (expectedPrev != null && remoteStatus != null && remoteStatus !== expectedPrev) {
        return {
          kind: CONFLICT_CLASS.STATUS_CONFLICT,
          reason: `Status drifted: expected "${friendlyStatusLabel(expectedPrev)}" but remote is "${friendlyStatusLabel(remoteStatus)}".`,
          canRetry: false,
        };
      }
      return {
        kind: CONFLICT_CLASS.VERSION_RETRY_SAFE,
        reason: "Status transition is still valid; only the version is stale. Reload & retry will refresh `if_version` and re-send the same status flip.",
        canRetry: true,
      };
    }

    // Value attempt: retry safe iff remote value at attempted path equals
    // whatever the client had cached when it composed the attempt.
    if (attempt && attempt.value !== undefined) {
      const remoteVal = remoteSnapshot?.valueAtPath;
      const cachedVal = attempt.cachedRemoteValue;
      if (remoteVal === undefined || cachedVal === undefined) {
        // Cannot prove safety without both anchors — be conservative.
        return {
          kind: CONFLICT_CLASS.VERSION_RETRY_BLOCKED,
          reason: "Cannot verify whether remote value drifted on this path; retry blocked for safety.",
          canRetry: false,
        };
      }
      if (valuesEqual(remoteVal, cachedVal)) {
        return {
          kind: CONFLICT_CLASS.VERSION_RETRY_SAFE,
          reason: "Remote value at this path is unchanged; another field caused the version bump. Reload & retry will refresh `if_version` and re-send your value.",
          canRetry: true,
        };
      }
      return {
        kind: CONFLICT_CLASS.VERSION_RETRY_BLOCKED,
        reason: "Remote value at this path differs from what you had loaded; retrying would overwrite the other admin's change. Review the diff and decide manually.",
        canRetry: false,
      };
    }

    // version_conflict with no recognizable attempt shape — block retry.
    return {
      kind: CONFLICT_CLASS.VERSION_RETRY_BLOCKED,
      reason: "Version drifted and the attempt payload is incomplete; retry blocked.",
      canRetry: false,
    };
  }

  return {
    kind: CONFLICT_CLASS.UNKNOWN,
    reason: "Conflict payload not recognized.",
    canRetry: false,
  };
};

// Shortcut helper. Useful in JSX render to gate a Retry button.
// Returns `{ canRetry, reason, kind }` — same shape as classifyConflict for symmetry.
export const computeRetrySafety = (params) => {
  const c = classifyConflict(params);
  return { canRetry: c.canRetry, reason: c.reason, kind: c.kind };
};

const conflictDiffModule = {
  CONFLICT_CLASS,
  classifyConflict,
  computeRetrySafety,
  valuesEqual,
  normalizeValue,
  friendlyStatusLabel,
  formatVersionDelta,
};

export default conflictDiffModule;
